import type { ReactNode } from 'react';
import {
  ArrowDownToLine,
  ArrowLeftRight,
  ArrowUpFromLine,
  BadgeCheck,
  Banknote,
  BarChart3,
  BedDouble,
  BookHeart,
  BookOpen,
  Boxes,
  Building,
  Building2,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  ClipboardCheck,
  ClipboardList,
  DatabaseZap,
  Eye,
  FileBarChart,
  FileCheck2,
  FileText,
  FlaskConical,
  Gauge,
  GitMerge,
  Globe,
  History,
  Hospital,
  KeyRound,
  Landmark,
  Languages,
  Layers,
  LayoutDashboard,
  LayoutGrid,
  ListChecks,
  ListOrdered,
  ListTree,
  Medal,
  MapPinned,
  Monitor,
  Network,
  Package,
  PackageOpen,
  PieChart,
  Pill,
  Receipt,
  ReceiptText,
  RefreshCw,
  Repeat,
  ScanEye,
  ScanLine,
  Scissors,
  Send,
  Settings,
  Settings2,
  Share2,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Smartphone,
  Split,
  Stethoscope,
  Syringe,
  TabletSmartphone,
  Tags,
  Target,
  Timer,
  TriangleAlert,
  Trophy,
  Truck,
  UserCog,
  Users,
  Wallet,
  Warehouse,
  Waypoints,
  Wrench,
  Accessibility,
} from 'lucide-react';

/** A sub-link under a module (usually a tab of the module page). */
export interface NavChild {
  to: string;
  labelKey: string;
  icon?: ReactNode;
  perms?: string[]; // any of
}

/**
 * The 9 areas of the system (docs/HMS_NOC_MIS_Unified_Design.md §3.1). The menu shows them in this
 * order, each under its own heading; Platform (administration) comes last.
 */
export const AREAS = ['home', 'patientAdmin', 'clinical', 'eye', 'inpatient', 'supplies', 'finance', 'reporting', 'engagement', 'platform'] as const;
export type Area = (typeof AREAS)[number];

/** Icon shown in front of each area heading. */
export const AREA_ICONS: Record<Area, ReactNode> = {
  home: <LayoutDashboard size={15} />,
  patientAdmin: <Users size={15} />,
  clinical: <Stethoscope size={15} />,
  eye: <Eye size={15} />,
  inpatient: <BedDouble size={15} />,
  supplies: <Boxes size={15} />,
  finance: <Wallet size={15} />,
  reporting: <BarChart3 size={15} />,
  engagement: <Smartphone size={15} />,
  platform: <Settings2 size={15} />,
};

export interface NavItem {
  to: string;
  labelKey: string;
  icon: ReactNode;
  perms?: string[]; // any of
  area: Area;
  children?: NavChild[];
}

const INV_READ = ['inventory.read'];
const DHIS2 = ['dhis2.admin', 'dhis2.submit', 'dhis2.approve'];
const NATIONAL = ['kpi.read', 'report.prepare', 'report.approve', ...DHIS2];

export const NAV: NavItem[] = [
  { to: '/', labelKey: 'nav.dashboard', icon: <LayoutDashboard size={20} />, perms: ['report.read'], area: 'home' },

  // 2. Patient administration
  { to: '/patients', labelKey: 'nav.patients', icon: <Users size={20} />, perms: ['patient.read'], area: 'patientAdmin' },
  {
    to: '/appointments',
    labelKey: 'nav.appointments',
    icon: <CalendarDays size={20} />,
    perms: ['appointment.read'],
    area: 'patientAdmin',
    children: [
      { to: '/appointments', icon: <ClipboardList size={16} />, labelKey: 'nav.appointmentList' },
      { to: '/appointments/dashboard', icon: <LayoutDashboard size={16} />, labelKey: 'nav.appointmentDashboard' },
    ],
  },
  {
    to: '/queue',
    labelKey: 'nav.queue',
    icon: <ListOrdered size={20} />,
    perms: ['queue.read', 'queue.kiosk', 'queue.display'],
    area: 'patientAdmin',
    children: [
      { to: '/queue', icon: <ListOrdered size={16} />, labelKey: 'nav.queueDashboard', perms: ['queue.read'] },
      { to: '/queue/kiosk', icon: <TabletSmartphone size={16} />, labelKey: 'nav.queueKiosk', perms: ['queue.kiosk'] },
      { to: '/queue/display', icon: <Monitor size={16} />, labelKey: 'nav.queueDisplay', perms: ['queue.read', 'queue.display'] },
      { to: '/queue/report', icon: <Timer size={16} />, labelKey: 'nav.queueReport', perms: ['queue.read'] },
    ],
  },
  { to: '/patients/duplicates', labelKey: 'nav.duplicates', icon: <GitMerge size={20} />, perms: ['patient.merge', 'patient.merge.approve'], area: 'patientAdmin' },

  // 3. Clinical care
  { to: '/encounters', labelKey: 'nav.encounters', icon: <Stethoscope size={20} />, perms: ['encounter.read'], area: 'clinical' },
  { to: '/lab', labelKey: 'nav.laboratory', icon: <FlaskConical size={20} />, perms: ['lab.read'], area: 'clinical' },
  {
    to: '/services',
    labelKey: 'nav.specialServices',
    icon: <ScanLine size={20} />,
    perms: ['service.order', 'service.perform', 'service.verify', 'service.manage'],
    area: 'clinical',
    children: [
      { to: '/services?tab=worklist', icon: <ListChecks size={16} />, labelKey: 'nav.serviceWorklist' },
      { to: '/services?tab=catalog', icon: <Tags size={16} />, labelKey: 'nav.serviceCatalog' },
    ],
  },
  { to: '/referrals', labelKey: 'nav.referrals', icon: <Share2 size={20} />, perms: ['referral.read', 'referral.manage'], area: 'clinical' },
  { to: '/follow-ups', labelKey: 'nav.followUps', icon: <CalendarClock size={20} />, perms: ['followup.manage'], area: 'clinical' },
  { to: '/pathways', labelKey: 'nav.treatmentCycles', icon: <Repeat size={20} />, perms: ['patient.read'], area: 'clinical' },
  { to: '/admin/diseases', labelKey: 'nav.diseases', icon: <BookHeart size={20} />, perms: ['disease.manage'], area: 'clinical' },

  // 4. Eye center
  {
    to: '/eye',
    labelKey: 'nav.eyeCenter',
    icon: <Eye size={20} />,
    perms: ['ophthalmology.read'],
    area: 'eye',
    children: [
      { to: '/eye?tab=overview', icon: <Gauge size={16} />, labelKey: 'eye.overview' },
      { to: '/eye?tab=exams', icon: <ScanEye size={16} />, labelKey: 'eye.examinations' },
      { to: '/eye?tab=surgeries', icon: <Scissors size={16} />, labelKey: 'eye.surgeries' },
      { to: '/eye?tab=injections', icon: <Syringe size={16} />, labelKey: 'eye.injections' },
      { to: '/eye?tab=followUps', icon: <CalendarCheck size={16} />, labelKey: 'eye.followUps' },
    ],
  },

  // 5. Inpatient
  {
    to: '/inpatient',
    labelKey: 'nav.inpatient',
    icon: <BedDouble size={20} />,
    perms: ['inpatient.read', 'ward.manage'],
    area: 'inpatient',
    children: [
      { to: '/inpatient?tab=board', icon: <LayoutGrid size={16} />, labelKey: 'nav.bedBoard', perms: ['inpatient.read'] },
      { to: '/inpatient?tab=admissions', icon: <ClipboardList size={16} />, labelKey: 'nav.admissions', perms: ['inpatient.read'] },
      { to: '/inpatient?tab=setup', icon: <Building size={16} />, labelKey: 'nav.wardSetup', perms: ['ward.manage'] },
    ],
  },

  // 6. Supplies
  { to: '/pharmacy', labelKey: 'nav.pharmacy', icon: <Pill size={20} />, perms: ['prescription.read', 'pharmacy.dispense'], area: 'supplies' },
  {
    to: '/pos',
    labelKey: 'nav.pos',
    icon: <ShoppingCart size={20} />,
    perms: ['sales.sell', 'sales.void', 'pos.manage', 'pos.stock'],
    area: 'supplies',
    children: [
      { to: '/pos', icon: <ShoppingBag size={16} />, labelKey: 'nav.posCounter', perms: ['sales.sell'] },
      { to: '/pos/stock', icon: <PackageOpen size={16} />, labelKey: 'nav.posStock', perms: ['pos.stock', 'pos.manage'] },
      { to: '/pos/catalog', icon: <BookOpen size={16} />, labelKey: 'nav.posCatalog', perms: ['pos.manage', 'pos.stock'] },
      { to: '/sales', icon: <History size={16} />, labelKey: 'nav.salesHistory', perms: ['sales.sell', 'sales.void'] },
    ],
  },
  {
    to: '/inventory',
    labelKey: 'nav.inventory',
    icon: <Boxes size={20} />,
    perms: ['inventory.read', 'purchase.request', 'purchase.approve', 'inventory.request', 'inventory.verify'],
    area: 'supplies',
    children: [
      { to: '/inventory?tab=dashboard', icon: <LayoutDashboard size={16} />, labelKey: 'inventory.tab.dashboard', perms: INV_READ },
      { to: '/inventory?tab=items', icon: <Package size={16} />, labelKey: 'nav.itemsPrices', perms: INV_READ },
      { to: '/inventory?tab=stock-in', icon: <ArrowDownToLine size={16} />, labelKey: 'inventory.tab.stockIn', perms: INV_READ },
      { to: '/inventory?tab=stock-out', icon: <ArrowUpFromLine size={16} />, labelKey: 'inventory.tab.stockOut', perms: ['inventory.read', 'inventory.request'] },
      { to: '/inventory?tab=transfers', icon: <ArrowLeftRight size={16} />, labelKey: 'inventory.tab.transfers', perms: INV_READ },
      { to: '/inventory?tab=to-check', icon: <ClipboardCheck size={16} />, labelKey: 'inventory.tab.toCheck', perms: ['inventory.verify'] },
      { to: '/inventory?tab=pos-refills', icon: <RefreshCw size={16} />, labelKey: 'inventory.tab.posRefills', perms: ['inventory.manage', 'inventory.verify'] },
      { to: '/inventory?tab=requests', icon: <FileText size={16} />, labelKey: 'inventory.tab.requests', perms: ['inventory.read', 'purchase.request', 'purchase.approve'] },
      { to: '/inventory?tab=orders', icon: <FileCheck2 size={16} />, labelKey: 'inventory.tab.orders', perms: ['inventory.read', 'purchase.order', 'purchase.approve'] },
      { to: '/inventory?tab=stock', icon: <Layers size={16} />, labelKey: 'inventory.tab.stock', perms: INV_READ },
      { to: '/inventory?tab=suppliers', icon: <Truck size={16} />, labelKey: 'inventory.tab.suppliers', perms: INV_READ },
      { to: '/inventory?tab=equipment', icon: <Wrench size={16} />, labelKey: 'inventory.tab.equipment', perms: INV_READ },
      { to: '/inventory?tab=alerts', icon: <TriangleAlert size={16} />, labelKey: 'inventory.tab.alerts', perms: INV_READ },
      { to: '/inventory?tab=stores', icon: <Warehouse size={16} />, labelKey: 'inventory.tab.stores', perms: INV_READ },
    ],
  },

  // 7. Finance
  { to: '/billing', labelKey: 'nav.billing', icon: <Receipt size={20} />, perms: ['billing.read'], area: 'finance' },
  {
    to: '/finance',
    labelKey: 'nav.finance',
    icon: <Wallet size={20} />,
    perms: ['finance.read', 'finance.manage', 'cash.session'],
    area: 'finance',
    children: [
      { to: '/finance?tab=dashboard', icon: <PieChart size={16} />, labelKey: 'finance.tab.dashboard', perms: ['finance.read'] },
      { to: '/finance?tab=prices', icon: <Tags size={16} />, labelKey: 'finance.tab.prices', perms: ['finance.read', 'finance.manage'] },
      { to: '/finance?tab=sessions', icon: <Banknote size={16} />, labelKey: 'finance.tab.sessions', perms: ['finance.read', 'cash.session'] },
      { to: '/finance?tab=expenses', icon: <ReceiptText size={16} />, labelKey: 'finance.tab.expenses', perms: ['finance.read', 'finance.manage', 'cash.session'] },
      { to: '/finance?tab=history', icon: <History size={16} />, labelKey: 'finance.tab.history', perms: ['finance.read', 'finance.manage'] },
    ],
  },

  // 8. Performance & national reporting
  { to: '/reports', labelKey: 'nav.reports', icon: <FileBarChart size={20} />, perms: ['report.read'], area: 'reporting' },
  {
    to: '/staff-kpi',
    labelKey: 'nav.staffKpi',
    icon: <Trophy size={20} />,
    perms: ['staffkpi.read', 'staffkpi.manage'],
    area: 'reporting',
    children: [
      { to: '/staff-kpi', icon: <LayoutDashboard size={16} />, labelKey: 'nav.staffDashboard' },
      { to: '/staff-kpi?tab=leaderboard', icon: <Medal size={16} />, labelKey: 'nav.staffLeaderboard' },
      { to: '/staff-kpi?tab=targets', icon: <Target size={16} />, labelKey: 'nav.staffTargets' },
    ],
  },
  {
    to: '/national-reporting',
    labelKey: 'nav.nationalReporting',
    icon: <Landmark size={20} />,
    perms: NATIONAL,
    area: 'reporting',
    children: [
      { to: '/national-reporting?tab=dashboard', icon: <Gauge size={16} />, labelKey: 'national.tab.dashboard', perms: NATIONAL },
      { to: '/national-reporting?tab=snapshots', icon: <FileCheck2 size={16} />, labelKey: 'national.tab.snapshots', perms: NATIONAL },
      { to: '/national-reporting?tab=catalog', icon: <ListTree size={16} />, labelKey: 'national.tab.catalog', perms: NATIONAL },
      { to: '/national-reporting?tab=batches', icon: <Send size={16} />, labelKey: 'national.tab.batches', perms: DHIS2 },
      { to: '/national-reporting?tab=mappings', icon: <Waypoints size={16} />, labelKey: 'national.tab.mappings', perms: DHIS2 },
      { to: '/national-reporting?tab=connections', icon: <DatabaseZap size={16} />, labelKey: 'national.tab.connections', perms: ['dhis2.admin'] },
    ],
  },

  // 9. Patient engagement
  { to: '/admin/patient-app', labelKey: 'nav.patientApp', icon: <Smartphone size={20} />, perms: ['portal.manage'], area: 'engagement' },

  // 1. Platform (administration)
  { to: '/admin/tenants', labelKey: 'nav.tenants', icon: <Network size={20} />, perms: ['tenant.manage'], area: 'platform' },
  { to: '/admin/hospital', labelKey: 'nav.hospital', icon: <Hospital size={20} />, perms: ['org.manage'], area: 'platform' },
  { to: '/admin/departments', labelKey: 'nav.departments', icon: <Building2 size={20} />, perms: ['org.manage'], area: 'platform' },
  { to: '/admin/practitioners', labelKey: 'nav.practitioners', icon: <BadgeCheck size={20} />, perms: ['org.manage'], area: 'platform' },
  { to: '/admin/users', labelKey: 'nav.users', icon: <UserCog size={20} />, perms: ['user.read'], area: 'platform' },
  { to: '/admin/roles', labelKey: 'nav.roles', icon: <KeyRound size={20} />, perms: ['role.manage'], area: 'platform' },
  { to: '/admin/queue-services', labelKey: 'nav.queueServices', icon: <Split size={20} />, perms: ['queue.configure'], area: 'platform' },
  { to: '/admin/disability-types', labelKey: 'nav.disabilityTypes', icon: <Accessibility size={20} />, perms: ['reference.manage'], area: 'platform' },
  { to: '/admin/locations', labelKey: 'nav.locations', icon: <MapPinned size={20} />, perms: ['location.manage'], area: 'platform' },
  { to: '/admin/translations', labelKey: 'nav.translations', icon: <Languages size={20} />, area: 'platform' },
  { to: '/admin/languages', labelKey: 'nav.languages', icon: <Globe size={20} />, perms: ['i18n.manage.global'], area: 'platform' },
  { to: '/admin/audit', labelKey: 'nav.auditLog', icon: <ShieldCheck size={20} />, perms: ['audit.read'], area: 'platform' },
  { to: '/settings', labelKey: 'nav.settings', icon: <Settings size={20} />, area: 'platform' },
];

/** Modules (and their sub-links) the user may open. */
export function visibleNav(canAny: (...p: string[]) => boolean): NavItem[] {
  return NAV.filter((n) => !n.perms || canAny(...n.perms)).map((n) =>
    n.children ? { ...n, children: n.children.filter((c) => !c.perms || canAny(...c.perms)) } : n,
  );
}

/** Path prefixes that belong to a module besides its own (e.g. /sales under the counter). */
export const NAV_ALIASES: Record<string, string> = { '/sales': '/pos' };
